/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { CommonModule } from '@angular/common';
import { booleanAttribute, Component, ElementRef, input, output, ViewChild } from '@angular/core';
import { cva, VariantProps } from 'class-variance-authority';

import { IconVariants } from '../icon/icon.component';
import { SpinnerIconComponent } from '../icons/spinner-icon/spinner-icon.component';

export const buttonVariants = cva(
  [
    'flex items-center gap-3 rounded-lg text-sm font-medium box-border',
    'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 outline-focus',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-primary text-whitetext shadow-md hover:bg-primary-hover focus-visible:bg-primary-hover',
        outline:
          'border border-primary bg-background-50 text-primary shadow-md hover:bg-ghost-hover focus-visible:bg-ghost-hover focus-visible:border-background-200',
        ghost:
          'border border-transparent hover:bg-ghost-hover text-primary focus-visible:border-background-200 focus-visible:bg-ghost-hover font-semibold [&]:focus-visible:outline-offset-1',
        navigation: 'border border-neutral-200 text-text hover:bg-neutral-50 dark:hover:bg-neutral-600 focus-visible:bg-neutral-50 dark:focus-visible:bg-neutral-700',
      },
      size: {
        medium: 'h-9 py-2 px-4 min-w-32',
        fit: 'w-fit h-fit p-2',
      },
      disabled: {
        false: null,
        true: '[&]:outline-disabled-button cursor-not-allowed',
      },
      destructive: {
        false: null,
        true: '[&]:outline-destructive',
      },
      spacing: {
        default: '',
        medium: 'my-4',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
      disabled: false,
      spacing: 'default',
    },
    compoundVariants: [
      {
        variant: 'primary',
        destructive: true,
        class: '[&]:hover:bg-destructive-primary-hover [&]:bg-destructive [&]:focus-visible:bg-destructive-primary-hover',
      },
      {
        variant: 'outline',
        destructive: true,
        class:
          '[&]:border-destructive [&]:text-destructive [&]:hover:bg-destructive-hover [&]:focus-visible:bg-destructive-hover',
      },
      {
        variant: 'ghost',
        destructive: true,
        class: '[&]:text-destructive [&]:hover:bg-destructive-hover [&]:focus-visible:bg-destructive-hover',
      },
      {
        variant: 'primary',
        disabled: true,
        class: '[&]:bg-disabled-button [&]:hover:bg-disabled-button/90 [&]:focus-visible:bg-disabled-button/90',
      },
      {
        variant: 'outline',
        disabled: true,
        class:
          '[&]:text-disabled-button [&]:border-disabled-button [&]:hover:bg-disabled-button/10 [&]:focus-visible:bg-disabled-button/10',
      },
      {
        variant: 'ghost',
        disabled: true,
        class: '[&]:text-disabled-button [&]:hover:bg-disabled-button/10 [&]:focus-visible:bg-disabled-button/10',
      },
    ],
  },
);
export type ButtonVariants = VariantProps<typeof buttonVariants>;

@Component({
  selector: 'ods-button',
  imports: [CommonModule, SpinnerIconComponent],
  host: { class: 'block w-fit' },
  template: `<button
    #nativeButton
    [type]="type()"
    [ngClass]="
      buttonVariants({ size: size(), variant: variant(), disabled: isDisabled, destructive: destructive(), spacing: spacing() })
    "
    [attr.aria-disabled]="isDisabled"
    [attr.aria-label]="ariaLabel() ?? null"
    [attr.aria-pressed]="ariaPressed()"
    [attr.aria-expanded]="ariaExpanded()"
    [attr.data-test-id]="dataTestId()"
    [attr.data-test-class]="dataTestClass()"
    (click)="onClick($event)"
  >
    @if (isLoading()) {
      <ods-spinner-icon color="disabled" [size]="spinnerSize()" data-test-class="spinner" />
    } @else {
      <ng-content select="[icon]" />
    }
    @if (text()) {
      <p class="grow">{{ text() }}</p>
    }
  </button>`,
})
export class ButtonComponent {
  @ViewChild('nativeButton') private readonly nativeButtonRef: ElementRef<HTMLButtonElement>;

  public readonly text = input<string>('');
  public readonly dataTestId = input<string>('');
  public readonly dataTestClass = input<string>('');
  public readonly disabled = input(false, { transform: booleanAttribute });
  public readonly isLoading = input(false, { transform: booleanAttribute });
  public readonly destructive = input(false, { transform: booleanAttribute });
  public readonly variant = input<ButtonVariants['variant']>();
  public readonly size = input<ButtonVariants['size']>();
  public readonly spacing = input<ButtonVariants['spacing']>();
  public readonly spinnerSize = input<IconVariants['size']>('medium');
  public readonly type = input<'button' | 'submit'>('button');
  public readonly ariaLabel = input<string>(null);
  public readonly ariaPressed = input<boolean>(null);
  public readonly ariaExpanded = input<boolean>(null);

  public readonly clicked = output<MouseEvent>();

  get isDisabled(): boolean {
    return this.disabled() || this.isLoading();
  }

  public onClick(e: MouseEvent): void {
    if (this.isDisabled) e.preventDefault();
    else this.clicked.emit(e);
  }

  public focus(): void {
    this.nativeButtonRef.nativeElement.focus();
  }

  public readonly buttonVariants = buttonVariants;
}
